import { useTranslation } from "react-i18next";
import { Badge, Tooltip, TooltipContent, TooltipTrigger, cn } from "@nexus/ui";
import type { PreflightScoreBand } from "../../types/preflight.types";
import { bandTone, scoreToBand } from "../../lib/score-band";

type BadgeVariant = React.ComponentProps<typeof Badge>["variant"];

const SCORE_MAX = 100;

/** Band tone → Adge badge variant (KTD11). Color is always paired with the band label. */
const BAND_BADGE_VARIANT: Record<ReturnType<typeof bandTone>, BadgeVariant> = {
  success: "status-connected",
  warning: "status-in-production",
  error: "status-error",
};

const SCORE_TONE_CLASS: Record<ReturnType<typeof bandTone>, string> = {
  success: "text-success",
  warning: "text-warning",
  error: "text-error",
};

const SCORE_BAR_FILL_CLASS: Record<ReturnType<typeof bandTone>, string> = {
  success: "bg-success",
  warning: "bg-warning",
  error: "bg-error",
};

export interface ScoreBadgeProps {
  score: number | null;
  band: PreflightScoreBand | null;
  /** Render an em dash when there is no score yet (non-terminal/unanalyzed). */
  emptyLabel?: string;
  /**
   * Inline row for tables; card layout for creative result headers. `inline`
   * renders a tooltip, so it needs a `TooltipProvider` ancestor.
   */
  variant?: "inline" | "card";
  className?: string;
}

/**
 * Renders a 0–100 score with its band. The card variant labels the band with a
 * badge; the inline variant trades the badge for a proportion bar, since a
 * per-row band chip repeats the number in a table full of scores.
 */
export function ScoreBadge({
  score,
  band,
  emptyLabel = "—",
  variant = "inline",
  className,
}: ScoreBadgeProps) {
  const { t } = useTranslation();
  if (score == null) {
    return <span className={cn("text-muted-foreground", className)}>{emptyLabel}</span>;
  }

  const resolved = band ?? scoreToBand(score);
  const rounded = Math.round(score);
  const tone = bandTone(resolved);

  if (variant === "card") {
    return (
      <div
        className={cn("flex flex-col gap-2", className)}
        aria-label={t("preflight.summary.scoreOutOf", { score: rounded })}
      >
        <div className="flex items-baseline gap-1.5">
          <span
            className={cn(
              "text-4xl font-semibold tabular-nums leading-none tracking-tight",
              SCORE_TONE_CLASS[tone],
            )}
          >
            {rounded}
          </span>
          <span className="text-muted-foreground text-sm tabular-nums">/ 100</span>
        </div>
        <Badge variant={BAND_BADGE_VARIANT[tone]}>
          {t(`preflight.band.${resolved}`)}
        </Badge>
      </div>
    );
  }

  const inlineLabel = t("preflight.summary.scoreWithBand", {
    score: rounded,
    band: t(`preflight.band.${resolved}`),
  });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn("inline-flex items-center gap-2 whitespace-nowrap", className)}
        >
          <span
            className={cn(
              "w-7 text-right text-sm font-semibold leading-none tabular-nums",
              SCORE_TONE_CLASS[tone],
            )}
          >
            {rounded}
          </span>
          <span
            className="bg-muted-foreground/25 h-1.5 w-14 shrink-0 overflow-hidden rounded-full"
            aria-hidden
          >
            <span
              className={cn("block h-full rounded-full", SCORE_BAR_FILL_CLASS[tone])}
              style={{ width: `${(rounded / SCORE_MAX) * 100}%` }}
            />
          </span>
          <span className="sr-only">{inlineLabel}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{inlineLabel}</TooltipContent>
    </Tooltip>
  );
}
